import { LinkButton } from "@/components/LinkButton";
import {
  isAssociatedWithProject,
  isOpenSlot,
} from "@/features/appointments/utils";
import { openBokaBookingModal } from "@/features/bookingModal/openBokaBookingModal";
import { useModals } from "@/features/modals/hooks";
import {
  type Appointment,
  type Customer,
  useAppointmentFromOpportunity,
} from "@repo/api";
import { Button, Loading } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ModalSheet,
  openNewProjectModal,
  useCustomer,
  useSettings,
} from "@repo/ui";
import { trackAnalytics, useTypesafeActions } from "@repo/utils";
import { isBefore } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppointmentActions } from "../useAppointmentActions";
import {
  type AppointmentModalState,
  type AppointmentModalStateActions,
  AppointmentModalStateReducer,
  appointmentModalStateActions,
} from "./components/AppointmentModalReducer";
import { AppointmentMoreActionButtons } from "./components/AppointmentMoreActionButtons";
import { CalendarAppointmentView } from "./components/CalendarAppointmentView";
import { ConvertToProjectButton } from "./components/ConvertToProjectButton";
import { DropInAppointmentEdit } from "./components/DropInAppointmentEdit";

interface CalendarAppointmentModalProps {
  isOpen: boolean;
  closeModal: () => void;
  appointment?: Appointment;
  dropInId?: number;
  mutateSearchAppointments?: () => void;
  onBookFollowUp?: (appointment: Appointment, customer?: Customer) => void;
}

const CalendarAppointmentPrimaryActionButton = ({
  state,
  closeModal,
}: {
  state: AppointmentModalState;
  closeModal: () => void;
}) => {
  const navigate = useNavigate();
  const { pushModal } = useModals();
  const { boka_config } = useSettings();
  const isBokaAppointmentIntegrationEnabled =
    boka_config?.enable_boka_appointment_modal_integration ?? false;
  const [isBokaLoading, setIsBokaLoading] = useState(false);
  const isAssociated = isAssociatedWithProject(state.opportunityId);
  const isDropIn = state.appointment?.type === "DROP_IN";
  const isOpenSlotBooking = isOpenSlot(
    state.appointment?.reasonCode,
    state.appointment?.orderNumber,
    state.appointment?.type
  );

  const onProjectCreated = (projectId: number) => {
    navigate(`/opportunity/${projectId}/`);
    trackAnalytics("new_project_created");
  };

  const onSelectProject = (projectId: number) => {
    navigate(`/opportunity/${projectId}/`);
    trackAnalytics("manage_project");
  };

  const handleBookBokaAppointment = async () => {
    if (!state.appointment) {
      return;
    }
    trackAnalytics("book_boka_appointment");

    setIsBokaLoading(true);
    try {
      await openBokaBookingModal({
        // TODO: hardcoded for Boka BE integration testing — derive from opportunity/store config
        postalCode: "4450-745",
        serviceProductId: "BUSINESS_PLANNING",
        bokaAppointmentId: state.appointment.id,
        coworkerEmail: state.appointment.assignedCoworker ?? "",
        locationType: "STORE",
        from: state.appointment.timeWindowStartTime ?? new Date().toISOString(),
        ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    } finally {
      setIsBokaLoading(false);
    }
  };

  if (!state.appointment) {
    return null;
  }

  if (isOpenSlotBooking) {
    if (isBokaAppointmentIntegrationEnabled) {
      return (
        <Button
          type="primary"
          text={t("action.book_appointment")}
          loading={isBokaLoading}
          disabled={isBokaLoading}
          onClick={() => {
            void handleBookBokaAppointment();
          }}
        />
      );
    }

    return (
      <Button
        type="primary"
        text={t("action.open_in_boka")}
        disabled={!state.appointment.appointmentLink}
        onClick={() => {
          window.open(state.appointment?.appointmentLink, "_blank");
          trackAnalytics("open_in_boka");
        }}
      />
    );
  }

  if (!isAssociated) {
    // unassociated drop-in appointments
    if (isDropIn) {
      return (
        <Button
          type="primary"
          text={t("action.add_customer")}
          onClick={() => {
            openNewProjectModal({
              pushModal,
              resetModal: true,
              onSelectProject,
              onProjectCreated,
              associatedDropInAppointment: state.appointment,
            });
          }}
        />
      );
    }

    // unassociated BOKA appointments (not open slot)
    if (!isOpenSlotBooking) {
      return (
        <ConvertToProjectButton
          state={state}
          navigate={navigate}
          closeModal={closeModal}
        />
      );
    }
  }

  return (
    <LinkButton
      to={`/opportunity/${state.opportunityId}`}
      type="primary"
      text={t("action.open_project")}
      onClick={() => {
        trackAnalytics("open_project_page");
      }}
    />
  );
};

const getView = ({
  state,
  actions,
  onUpdate,
  onDelete,
  closeModal,
  mutateSearchAppointments,
  onBookFollowUp,
  isLoadingDetails,
}: {
  state: AppointmentModalState;
  actions: AppointmentModalStateActions;
  onUpdate: () => void;
  onDelete: () => void;
  closeModal: () => void;
  mutateSearchAppointments?: () => void;
  onBookFollowUp?: (appointment: Appointment, customer?: Customer) => void;
  isLoadingDetails?: boolean;
}) => {
  if (!state.appointment) {
    return {
      onBack: undefined,
      footer: undefined,
      view: <Loading text={t("prompt.loading")} />,
    };
  }

  const isValid = () => {
    if (state.updatedTask) {
      if (
        state.updatedTask?.taskStartDateTime !== undefined &&
        state.updatedTask.taskEndDateTime !== undefined &&
        isBefore(
          new Date(state.updatedTask.taskStartDateTime),
          new Date(state.updatedTask.taskEndDateTime)
        )
      ) {
        return true;
      }
    } else if (state.updatedAppointment) {
      if (
        state.updatedAppointment?.timeWindowStartTime !== undefined &&
        state.updatedAppointment.timeWindowEndTime !== undefined &&
        isBefore(
          new Date(state.updatedAppointment.timeWindowStartTime),
          new Date(state.updatedAppointment.timeWindowEndTime)
        )
      ) {
        return true;
      }
    }
  };

  switch (state.step) {
    case "VIEW":
      return {
        onBack: undefined,
        footer: (
          <>
            <CalendarAppointmentPrimaryActionButton
              state={state}
              closeModal={closeModal}
            />

            <Button
              text={t("label.more_actions")}
              size="small"
              type="secondary"
              onClick={() => {
                actions.setStep("MORE_ACTIONS");
                trackAnalytics("appointmnet_more_actions");
              }}
            />
          </>
        ),

        view: (
          <CalendarAppointmentView
            state={state}
            actions={actions}
            onDelete={onDelete}
            isLoadingDetails={isLoadingDetails}
          />
        ),
      };

    case "EDIT": {
      return {
        onBack: () => actions.setStep("VIEW"),
        footer: (
          <Button
            type="primary"
            text={t("action.save")}
            onClick={onUpdate}
            loading={state.loading === "UPDATING"}
            disabled={!isValid}
          />
        ),
        view: <DropInAppointmentEdit state={state} actions={actions} />,
      };
    }
    case "MORE_ACTIONS":
      return {
        onBack: () => actions.setStep("VIEW"),
        footer: undefined,
        view: (
          <AppointmentMoreActionButtons
            state={state}
            actions={actions}
            onClose={closeModal}
            mutateSearchAppointments={mutateSearchAppointments}
            customer={state.customer}
            onBookFollowUp={onBookFollowUp}
          />
        ),
      };
  }
};

export const CalendarAppointmentModal = ({
  isOpen,
  closeModal,
  appointment,
  mutateSearchAppointments,
  onBookFollowUp,
}: CalendarAppointmentModalProps) => {
  const { customer } = useCustomer(
    appointment?.opportunityId,
    appointment?.orderNumber,
    true
  );

  const [state, actions] = useTypesafeActions<
    AppointmentModalState,
    AppointmentModalStateActions
  >(
    AppointmentModalStateReducer,
    {
      step: "VIEW",
      appointment,
      opportunityId: appointment?.opportunityId,
      loading: undefined,
      updatedTask: undefined,
      updatedAppointment: undefined,
      customer: customer,
      context: "CALENDAR",
    },
    appointmentModalStateActions
  );

  const { data: allDetailsOfAppointment, isLoading: isLoadingDetails } =
    useAppointmentFromOpportunity(appointment);

  useEffect(() => {
    if (allDetailsOfAppointment) {
      actions.setAppointment(allDetailsOfAppointment);
      if (allDetailsOfAppointment.opportunityId !== undefined) {
        actions.setOpportunityId(allDetailsOfAppointment.opportunityId);
      }
      if (state.step !== "EDIT") {
        actions.setUpdatedAppointment(undefined);
      }
    }
  }, [appointment, allDetailsOfAppointment, actions, state.step]);

  const { onUpdate, onDelete } = useAppointmentActions(
    state,
    actions,
    closeModal,
    mutateSearchAppointments
  );

  const view = getView({
    state,
    actions,
    onUpdate,
    onDelete,
    closeModal,
    mutateSearchAppointments,
    onBookFollowUp,
    isLoadingDetails,
  });

  return (
    <ModalSheet
      visible={isOpen}
      title={
        state.appointment
          ? state.appointment.orderNumber
            ? t("label.appointment_with_number", {
                number: state.appointment.orderNumber,
              })
            : t("label.appointment")
          : "-"
      }
      onClose={closeModal}
      onBack={view.onBack}
      footer={view.footer}
    >
      {view.view}
    </ModalSheet>
  );
};
